import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Users, Plus, X, ChevronRight, CalendarDays, RefreshCw,
    Filter, Archive, Trash2, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";

const ENROLL_BADGE = {
    active: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    withdrawn: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    completed: "bg-sky-400/10 border-sky-400/30 text-sky-400",
    archived: "bg-white/[0.03] border-white/10 text-white/40",
};

export default function PatientSchedulingPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterProtocol, setFilterProtocol] = useState("");
    const [filterStatus, setFilterStatus] = useState("active");
    const [q, setQ] = useState("");
    const [enrollOpen, setEnrollOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filterProtocol) params.set("protocol_id", filterProtocol);
            if (filterStatus) params.set("status", filterStatus);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/admin/scheduling/enrollments?${params.toString()}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [filterProtocol, filterStatus, q]);

    useEffect(() => { load(); }, [load]);

    const protocolFacets = useMemo(() => {
        const m = new Map();
        for (const it of items) {
            if (it.protocol_id) m.set(it.protocol_id, it.protocol_number || it.protocol_id);
        }
        return Array.from(m.entries());
    }, [items]);

    const archive = async (row) => {
        if (!confirm(`Archive enrollment for "${row.subject_ref}"?\n\nThe subject and visit history are preserved.`)) return;
        try {
            await api.delete(`/admin/scheduling/enrollments/${row.id}`);
            toast.success("Archived");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    const hardDelete = async (row) => {
        if (!confirm(`PERMANENTLY delete the enrollment for "${row.subject_ref}"?\n\nVisit history is lost. The subject's datalake row is kept.`)) return;
        try {
            await api.delete(`/admin/scheduling/enrollments/${row.id}?hard=true`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    return (
        <div className="px-8 py-8" data-testid="patient-scheduling-page">
            <div className="flex items-start justify-between mb-7">
                <div>
                    <h1 className="text-2xl font-display tracking-tight mb-1">Patient Scheduling</h1>
                    <p className="text-sm text-white/50">
                        Subject-to-protocol enrollments with auto-computed visit calendars. Each visit shows the generated forms required at that visit.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        data-testid="scheduling-refresh-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-xs text-white/70 hover:text-white hover:bg-white/5"
                    >
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={() => setEnrollOpen(true)}
                        data-testid="scheduling-enroll-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] text-sm font-medium"
                    >
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Enroll subject
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6 p-3 rounded-md border border-white/10 bg-white/[0.02]">
                <Filter className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} />
                <select
                    value={filterProtocol}
                    onChange={(e) => setFilterProtocol(e.target.value)}
                    data-testid="scheduling-filter-protocol"
                    className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs text-white/80"
                >
                    <option value="">All protocols</option>
                    {protocolFacets.map(([id, label]) => (
                        <option key={id} value={id}>{label}</option>
                    ))}
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="scheduling-filter-status"
                    className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs text-white/80"
                >
                    <option value="">Any status</option>
                    <option value="active">active</option>
                    <option value="withdrawn">withdrawn</option>
                    <option value="completed">completed</option>
                    <option value="archived">archived</option>
                </select>
                <input
                    type="text"
                    placeholder="Search subject ref…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && load()}
                    data-testid="scheduling-search-input"
                    className="bg-[#0a0a0a] border border-white/15 rounded px-2 py-1 text-xs text-white/80 w-56"
                />
                <span className="ml-auto text-xs text-white/40">
                    {items.length} enrollment{items.length === 1 ? "" : "s"}
                </span>
            </div>

            {loading ? (
                <div className="text-white/40 text-sm">Loading enrollments…</div>
            ) : items.length === 0 ? (
                <div className="p-10 rounded-md border border-dashed border-white/15 text-center" data-testid="scheduling-empty-state">
                    <Users className="w-8 h-8 mx-auto text-white/20 mb-3" strokeWidth={1.5} />
                    <div className="text-sm text-white/60 mb-1">No enrollments yet</div>
                    <div className="text-xs text-white/40 mb-4">
                        Click <strong>Enroll subject</strong> to anchor a subject's visit calendar to a protocol.
                    </div>
                    <button
                        type="button"
                        onClick={() => setEnrollOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] text-sm font-medium"
                    >
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                        Enroll subject
                    </button>
                </div>
            ) : (
                <div className="rounded-md border border-white/10 bg-white/[0.02] overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-white/40 text-xs">
                                <th className="px-5 py-2 font-medium">Subject</th>
                                <th className="px-3 py-2 font-medium">Protocol</th>
                                <th className="px-3 py-2 font-medium">Cohort</th>
                                <th className="px-3 py-2 font-medium">Baseline</th>
                                <th className="px-3 py-2 font-medium">Next visit</th>
                                <th className="px-3 py-2 font-medium">Progress</th>
                                <th className="px-3 py-2 font-medium">Status</th>
                                <th className="px-3 py-2 font-medium w-px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row) => (
                                <EnrollmentRow
                                    key={row.id}
                                    row={row}
                                    onArchive={archive}
                                    onDelete={hardDelete}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {enrollOpen && (
                <EnrollSubjectModal
                    onClose={() => setEnrollOpen(false)}
                    onDone={() => { setEnrollOpen(false); load(); }}
                />
            )}
        </div>
    );
}

function EnrollmentRow({ row, onArchive, onDelete }) {
    const counts = row.visits_by_status || {};
    const total = row.visits_total || 0;
    const done = counts.done || 0;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (
        <tr className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`enrollment-row-${row.id}`}>
            <td className="px-5 py-2.5 font-mono text-xs">
                <Link
                    to={`/scheduling/${row.id}`}
                    className="text-white hover:text-violet-400"
                    data-testid={`enrollment-open-${row.id}`}
                >
                    {row.subject_ref}
                </Link>
            </td>
            <td className="px-3 py-2.5 font-mono text-xs text-white/70">
                {row.protocol_number || row.protocol_id?.slice(0, 8) + "…"}
            </td>
            <td className="px-3 py-2.5 text-xs text-white/60">{row.cohort || "—"}</td>
            <td className="px-3 py-2.5 font-mono text-xs text-white/70">{row.baseline_date}</td>
            <td className="px-3 py-2.5 text-xs">
                {row.next_visit ? (
                    <span>
                        <span className="text-white/80">{row.next_visit.name}</span>
                        <span className="text-white/40 ml-1.5">{row.next_visit.scheduled_date || "—"}</span>
                    </span>
                ) : (
                    <span className="text-white/30">—</span>
                )}
            </td>
            <td className="px-3 py-2.5 text-xs">
                <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-emerald-400/60" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-white/60 font-mono text-[11px]">{done}/{total}</span>
                </div>
            </td>
            <td className="px-3 py-2.5 text-xs">
                <span className={`px-1.5 py-0.5 rounded border ${ENROLL_BADGE[row.status] || ENROLL_BADGE.active}`}>
                    {row.status}
                </span>
            </td>
            <td className="px-3 py-2.5 whitespace-nowrap">
                <div className="flex items-center justify-end gap-1">
                    <Link
                        to={`/scheduling/${row.id}`}
                        title="Open"
                        className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white"
                        data-testid={`enrollment-edit-${row.id}`}
                    >
                        <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                    {row.status !== "archived" && (
                        <button
                            onClick={() => onArchive(row)}
                            title="Archive"
                            className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-amber-400"
                            data-testid={`enrollment-archive-${row.id}`}
                        >
                            <Archive className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                    )}
                    <button
                        onClick={() => onDelete(row)}
                        title="Permanently delete"
                        className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-red-400"
                        data-testid={`enrollment-delete-${row.id}`}
                    >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                </div>
            </td>
        </tr>
    );
}

function EnrollSubjectModal({ onClose, onDone }) {
    const [protocols, setProtocols] = useState([]);
    const [protocolId, setProtocolId] = useState("");
    const [subjectRef, setSubjectRef] = useState("");
    const [baselineDate, setBaselineDate] = useState(new Date().toISOString().slice(0, 10));
    const [cohort, setCohort] = useState("");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/protocols?limit=200");
                // Only show protocols with at least 1 visit
                setProtocols((data.items || []).filter((p) => (p.visits || []).length > 0));
            } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            } finally { setLoading(false); }
        })();
    }, []);

    const selectedProtocol = useMemo(
        () => protocols.find((p) => p.id === protocolId), [protocolId, protocols]
    );

    const submit = async (e) => {
        e?.preventDefault();
        if (!protocolId || !subjectRef.trim() || !baselineDate) {
            toast.error("Protocol, subject ref, and baseline date are required");
            return;
        }
        setSubmitting(true);
        try {
            const body = {
                protocol_id: protocolId,
                subject_ref: subjectRef.trim(),
                baseline_date: baselineDate,
            };
            if (cohort.trim()) body.cohort = cohort.trim();
            if (notes.trim()) body.notes = notes.trim();
            if (selectedProtocol?.company_id) body.client_id = selectedProtocol.company_id;
            const { data } = await api.post("/admin/scheduling/enrollments", body);
            toast.success(data.reused
                ? `Enrollment already existed — opening it`
                : `Enrolled ${data.subject_ref} · ${data.visits_total} visit${data.visits_total === 1 ? "" : "s"} planned`
            );
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="enroll-subject-modal">
            <div className="w-full max-w-lg rounded-lg border border-white/15 bg-[#0a0a0a] shadow-2xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-violet-400" strokeWidth={1.75} />
                        <h2 className="text-sm font-medium">Enroll subject into a protocol</h2>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="enroll-close-btn">
                        <X className="w-4 h-4 text-white/60" strokeWidth={1.5} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4 text-xs">
                    <p className="text-white/50 -mt-1">
                        The visit calendar is auto-computed from the protocol's visit schedule
                        (`baseline_date + day_offset_from_baseline`). You can re-baseline or mark
                        visits done / missed / skipped on the enrollment page.
                    </p>
                    {loading ? (
                        <div className="text-white/40">Loading protocols…</div>
                    ) : (
                        <>
                            <div>
                                <label className="label-xs text-white/50 block mb-1">Protocol</label>
                                <select
                                    value={protocolId}
                                    onChange={(e) => setProtocolId(e.target.value)}
                                    data-testid="enroll-protocol-select"
                                    className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 text-sm"
                                >
                                    <option value="">Select a protocol…</option>
                                    {protocols.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.protocol_number || p.id.slice(0, 8)}
                                            {p.sponsor ? ` — ${p.sponsor}` : ""}
                                            {` · ${(p.visits || []).length} visit${(p.visits || []).length === 1 ? "" : "s"}`}
                                        </option>
                                    ))}
                                </select>
                                {protocols.length === 0 && (
                                    <p className="text-amber-400/80 mt-1">
                                        No protocols with a visit schedule — ingest one first.
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-xs text-white/50 block mb-1">Subject ref</label>
                                    <input
                                        type="text"
                                        value={subjectRef}
                                        onChange={(e) => setSubjectRef(e.target.value)}
                                        placeholder="S-001"
                                        data-testid="enroll-subject-ref-input"
                                        className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 text-sm font-mono"
                                    />
                                </div>
                                <div>
                                    <label className="label-xs text-white/50 block mb-1">Baseline date</label>
                                    <input
                                        type="date"
                                        value={baselineDate}
                                        onChange={(e) => setBaselineDate(e.target.value)}
                                        data-testid="enroll-baseline-date-input"
                                        className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 text-sm font-mono"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="label-xs text-white/50 block mb-1">Cohort (optional)</label>
                                <input
                                    type="text"
                                    value={cohort}
                                    onChange={(e) => setCohort(e.target.value)}
                                    placeholder="e.g. Arm A — 50 mg"
                                    data-testid="enroll-cohort-input"
                                    className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="label-xs text-white/50 block mb-1">Notes (optional)</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={2}
                                    data-testid="enroll-notes-input"
                                    className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2 text-sm"
                                />
                            </div>
                            {selectedProtocol && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded border border-violet-400/15 bg-violet-400/[0.03] text-white/70">
                                    <FlaskConical className="w-3.5 h-3.5 text-violet-400" strokeWidth={1.75} />
                                    <span>
                                        Will materialise <strong>{(selectedProtocol.visits || []).length}</strong> visits.
                                        Tenant: <span className="font-mono">{selectedProtocol.company_id}</span>.
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-2 rounded-md text-white/70 hover:text-white"
                        >Cancel</button>
                        <button
                            type="submit"
                            disabled={submitting || loading || !protocolId || !subjectRef.trim()}
                            data-testid="enroll-submit-btn"
                            className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
                            {submitting ? "Enrolling…" : "Enroll subject"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
